import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, ElementRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Optional, ViewChildren, Input, Output, NgModule } from '@angular/core';
import { __decorate } from 'tslib';
import { NgIf, NgClass, NgForOf } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { thumbMotion } from 'ng-zorro-antd/core/animation';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i4 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { InputBoolean } from 'ng-zorro-antd/core/util';
import * as i3 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import * as i2 from '@angular/cdk/bidi';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
const _c0 = ["itemLabels"];
const _c1 = () => ({
  "ant-segmented-thumb": true,
  "ant-segmented-thumb-motion": true
});
const _c2 = (a0, a1) => ({
  "ant-segmented-item": true,
  "ant-segmented-item-selected": a0,
  "ant-segmented-item-disabled": a1
});
const _c3 = (a0, a1) => ({
  $implicit: a0,
  index: a1
});
function NzSegmentedComponent_div_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵlistener("@thumbMotion.done", function NzSegmentedComponent_div_1_Template_div_animation_thumbMotion_done_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.handleThumbAnimationDone($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction0(2, _c1))("@thumbMotion", ctx_r1.animationState);
  }
}
function NzSegmentedComponent_label_2_ng_container_4_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r5 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r5.label, " ");
  }
}
function NzSegmentedComponent_label_2_ng_container_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "span", 9);
    i0.ɵɵelement(2, "span", 10);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "span");
    i0.ɵɵtemplate(4, NzSegmentedComponent_label_2_ng_container_4_ng_container_4_Template, 2, 1, "ng-container", 11);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext();
    const item_r5 = ctx_r6.$implicit;
    const i_r6 = ctx_r6.index;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("nzType", item_r5.icon);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r5.useTemplate && ctx_r1.nzLabelTemplate)("nzStringTemplateOutletContext", i0.ɵɵpureFunction2(3, _c3, item_r5, i_r6));
  }
}
function NzSegmentedComponent_label_2_ng_template_5_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const item_r5 = i0.ɵɵnextContext(2).$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r5.label, " ");
  }
}
function NzSegmentedComponent_label_2_ng_template_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzSegmentedComponent_label_2_ng_template_5_ng_container_0_Template, 2, 1, "ng-container", 11);
  }
  if (rf & 2) {
    const ctx_r6 = i0.ɵɵnextContext();
    const item_r5 = ctx_r6.$implicit;
    const i_r6 = ctx_r6.index;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("nzStringTemplateOutlet", item_r5.useTemplate && ctx_r1.nzLabelTemplate)("nzStringTemplateOutletContext", i0.ɵɵpureFunction2(2, _c3, item_r5, i_r6));
  }
}
function NzSegmentedComponent_label_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "label", 5, 0);
    i0.ɵɵelement(2, "input", 6);
    i0.ɵɵelementStart(3, "div", 7);
    i0.ɵɵlistener("click", function NzSegmentedComponent_label_2_Template_div_click_3_listener() {
      const ctx_r3 = i0.ɵɵrestoreView(_r3);
      const item_r5 = ctx_r3.$implicit;
      const i_r6 = ctx_r3.index;
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(!item_r5.disabled && ctx_r1.handleOptionClick(i_r6));
    });
    i0.ɵɵtemplate(4, NzSegmentedComponent_label_2_ng_container_4_Template, 5, 6, "ng-container", 8)(5, NzSegmentedComponent_label_2_ng_template_5_Template, 1, 5, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const item_r5 = ctx.$implicit;
    const i_r6 = ctx.index;
    const else_template_r8 = i0.ɵɵreference(6);
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(4, _c2, i_r6 === ctx_r1.selectedIndex, !!ctx_r1.nzDisabled || item_r5.disabled));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("checked", i_r6 === ctx_r1.selectedIndex);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", item_r5.icon)("ngIfElse", else_template_r8);
  }
}
function normalizeOptions(unnormalized) {
  return unnormalized.map(item => {
    if (typeof item === 'string' || typeof item === 'number') {
      return {
        label: `${item}`,
        value: item
      };
    }
    return item;
  });
}
const NZ_CONFIG_MODULE_NAME = 'segmented';
let NzSegmentedComponent = /*#__PURE__*/(() => {
  class NzSegmentedComponent {
    constructor(nzConfigService, cdr, directionality) {
      this.nzConfigService = nzConfigService;
      this.cdr = cdr;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.nzBlock = false;
      this.nzDisabled = false;
      this.nzOptions = [];
      this.nzSize = 'default';
      this.nzLabelTemplate = null;
      this.nzValueChange = new EventEmitter();
      this.dir = 'ltr';
      this.selectedIndex = 0;
      this.transitionedToIndex = -1;
      this.animationState = null;
      this.normalizedOptions = [];
      this.destroy$ = new Subject();
      this.onChange = () => {};
      this.onTouched = () => {};
      this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
    }
    ngOnChanges(changes) {
      const {
        nzOptions
      } = changes;
      if (nzOptions) {
        this.normalizedOptions = normalizeOptions(nzOptions.currentValue);
      }
    }
    handleOptionClick(index) {
      if (this.nzDisabled) {
        return;
      }
      this.changeSelectedIndex(index);
      this.onChange(index);
      this.nzValueChange.emit(index);
    }
    handleThumbAnimationDone(e) {
      if (e.fromState === 'from') {
        this.selectedIndex = this.transitionedToIndex;
        this.transitionedToIndex = -1;
        this.animationState = null;
        this.cdr.detectChanges();
      }
    }
    writeValue(value) {
      if (typeof value === 'number' && value > -1) {
        this.changeSelectedIndex(value);
        this.cdr.markForCheck();
      }
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    changeSelectedIndex(index) {
      if (!this.listOfOptions || this.selectedIndex === -1 || this.selectedIndex === index) {
        return;
      }
      this.animationState = {
        value: 'from',
        params: getThumbAnimationProps(this.listOfOptions.get(this.selectedIndex).nativeElement)
      };
      this.selectedIndex = -1;
      this.cdr.detectChanges();
      this.animationState = {
        value: 'to',
        params: getThumbAnimationProps(this.listOfOptions.get(index).nativeElement)
      };
      this.transitionedToIndex = index;
      this.cdr.detectChanges();
    }
    static {
      this.ɵfac = function NzSegmentedComponent_Factory(t) {
        return new (t || NzSegmentedComponent)(i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i2.Directionality, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSegmentedComponent,
        selectors: [["nz-segmented"]],
        viewQuery: function NzSegmentedComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5, ElementRef);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.listOfOptions = _t);
          }
        },
        hostAttrs: [1, "ant-segmented"],
        hostVars: 10,
        hostBindings: function NzSegmentedComponent_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵclassProp("ant-segmented-disabled", !!ctx.nzDisabled)("ant-segmented-rtl", ctx.dir === "rtl")("ant-segmented-lg", ctx.nzSize === "large")("ant-segmented-sm", ctx.nzSize === "small")("ant-segmented-block", !!ctx.nzBlock);
          }
        },
        inputs: {
          nzBlock: "nzBlock",
          nzDisabled: "nzDisabled",
          nzOptions: "nzOptions",
          nzSize: "nzSize",
          nzLabelTemplate: "nzLabelTemplate"
        },
        outputs: {
          nzValueChange: "nzValueChange"
        },
        exportAs: ["nzSegmented"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => NzSegmentedComponent),
          multi: true
        }]), i0.ɵɵNgOnChangesFeature, i0.ɵɵStandaloneFeature],
        decls: 3,
        vars: 2,
        consts: [["itemLabels", ""], ["else_template", ""], [1, "ant-segmented-group"], [3, "ngClass", 4, "ngIf"], [3, "ngClass", 4, "ngFor", "ngForOf"], [3, "ngClass"], ["type", "radio", 1, "ant-segmented-item-input", 3, "checked"], [1, "ant-segmented-item-label", 3, "click"], [4, "ngIf", "ngIfElse"], [1, "ant-segmented-item-icon"], ["nz-icon", "", 3, "nzType"], [4, "nzStringTemplateOutlet", "nzStringTemplateOutletContext"]],
        template: function NzSegmentedComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 2);
            i0.ɵɵtemplate(1, NzSegmentedComponent_div_1_Template, 1, 3, "div", 3)(2, NzSegmentedComponent_label_2_Template, 7, 7, "label", 4);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.animationState);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", ctx.normalizedOptions);
          }
        },
        dependencies: [NgIf, NgClass, NgForOf, NzIconModule, i3.NzIconDirective, NzOutletModule, i4.NzStringTemplateOutletDirective],
        encapsulation: 2,
        data: {
          animation: [thumbMotion]
        },
        changeDetection: 0
      });
    }
  }
  __decorate([InputBoolean()], NzSegmentedComponent.prototype, "nzBlock", void 0);
  __decorate([InputBoolean()], NzSegmentedComponent.prototype, "nzDisabled", void 0);
  __decorate([WithConfig()], NzSegmentedComponent.prototype, "nzSize", void 0);
  return NzSegmentedComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function getThumbAnimationProps(element) {
  return {
    transform: element.offsetLeft,
    width: element.clientWidth
  };
}

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSegmentedModule = /*#__PURE__*/(() => {
  class NzSegmentedModule {
    static {
      this.ɵfac = function NzSegmentedModule_Factory(t) {
        return new (t || NzSegmentedModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzSegmentedModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [NzSegmentedComponent]
      });
    }
  }
  return NzSegmentedModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzSegmentedComponent, NzSegmentedModule, normalizeOptions };
